
import { defineComponent, ref, reactive, onMounted, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { useHead } from '@vueuse/head'

import { RepositoryFactory } from '@/repositories/RepositoryFactory'
const SeasonGameRepository = RepositoryFactory.get('seasonGame')
const SeasonGroupRepository = RepositoryFactory.get('seasonGroup')
const SeasonGroupingRepository = RepositoryFactory.get('seasonGrouping')
const FieldRepository = RepositoryFactory.get('field')

import BaseBreadcrumb, { Breadcrumb } from '@/components/BaseBreadcrumb.vue'


import { Season } from '@/types/season'
import { GameForm } from '@/types/gameForm'
import { Custom } from '@/types/custom'
import { Group } from '@/types/group'
import { EntryTeam } from '@/types/entryTeam'
import { Field } from '@/types/field'

type ScoreType =
  | 'home_1st_score'
  | 'home_2nd_score'
  | 'home_ex_1st_score'
  | 'home_ex_2nd_score'
  | 'away_1st_score'
  | 'away_2nd_score'
  | 'away_ex_1st_score'
  | 'away_ex_2nd_score'

export default defineComponent({
  name: 'SeasonGameDetail',
  inheritAttrs: false,
  components: {
    BaseBreadcrumb,
  },
  setup() {
    const route = useRoute()
    const store = useStore()

    onMounted(() => {
      getGroups()
      getGroup()
      getGame()
    })

    const game: {
      data?: GameForm
    } = reactive({
      data: undefined
    })

    const getGame = async () => {
      try {
        const { season_id, game_id } = route.params

        const res = await SeasonGameRepository.getGame(season_id, game_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          const gameData = data.game

          game.data = {
            id: gameData.id,
            group_id: gameData.group_id,
            priority: gameData.priority,
            match_no: gameData.match_no,
            date: gameData.date,
            field_id: gameData.field_id,
            home_team_id: gameData.home?.entry_team_id,
            away_team_id: gameData.away?.entry_team_id,
            home_total_score: gameData.home_total_score,
            away_total_score: gameData.away_total_score,
            official: gameData.official,
            refree: gameData.refree,
            option: gameData.option,
            time: gameData.time,
            tournament_priority: gameData.tournament_priority,
            seed: gameData.seed
          }
        }
      } catch (error) {
        //
      }
    }

    const updateGame = async () => {
      try {
        const { season_id } = route.params

        const dataObj = game.data

        const res = await SeasonGameRepository.updateGame(season_id, dataObj)

        if (res.status === 200) {
          store.dispatch('addFlashMessage', {
            type: 'success',
            message: '試合情報を保存しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const season: {
      data?: Season
    } = reactive({
      data: undefined
    })

    const groups: {
      custom?: Custom
      customList: Custom[]
      group?: Group
      groupList: Group[]
    } = reactive({
      custom: undefined,
      customList: [],
      group: undefined,
      groupList: [],
    })

    const getGroups = async () => {
      try {
        const { season_id } = route.params
        const group_id = route.query.group

        const res = await SeasonGroupRepository.getGroups(season_id, group_id)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          season.data = data.season
          groups.customList = data.groups

          for (let i=0; i < groups.customList.length; i++) {
            const hasCurrentGroup = groups.customList[i].groups.some((group: Group) => String(group.id) === group_id)

            if (hasCurrentGroup) {
              groups.custom = groups.customList[i]
              groups.groupList = groups.customList[i].groups
            }
          }

          getFields()
        }
      } catch (error) {
        //
      }
    }

    const getGroup = async () => {
      try {
        const { season_id } = route.params
        const group_id = route.query.group

        const dataObj = {
          group_id: group_id
        }

        const res = await SeasonGroupingRepository.getGrouping(season_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          groups.group = data.group
          entries.list = data.teams
        }
      } catch (error) {
        //
      }
    }

    const entries: {
      list: EntryTeam[]
    } = reactive({
      list: []
    })

    const fields: {
      list: Field[]
    } = reactive({
      list: []
    })

    const getFields = async () => {
      try {
        const dataObj = {
          field_group_id: groups.custom?.field_group_id
        }

        const res = await FieldRepository.getFields(dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          fields.list = data.fields
        }
      } catch (error) {
        //
      }
    }

    const onInputScore = (scoreType: ScoreType, teamType: 'home' | 'away' | 'pk') => {
      if (game.data?.option == undefined) return

      const inputScore: any = game.data?.option[scoreType]

      if (inputScore != undefined && typeof inputScore !== 'number') {
        const halfSizeNumber = inputScore.replace(/[０-９]/g, (s: any) => String.fromCharCode(s.charCodeAt(0) - 65248)).replace(/\D/g,'')
        game.data.option[scoreType] = halfSizeNumber
      }

      if (teamType !== 'pk') getTotalScore(teamType)
    }

    const getTotalScore = (teamType: 'home' | 'away') => {
      if (game.data == undefined) return

      if (teamType === 'home') {
        let homeScores: any[] = [
          game.data.option?.home_1st_score,
          game.data.option?.home_2nd_score,
          game.data.option?.home_ex_1st_score,
          game.data.option?.home_ex_2nd_score
        ]

        const notPlayed = homeScores.every((score: any) => score === null || score === '')

        if (notPlayed) {
          game.data.home_total_score = undefined
        } else {
          homeScores = homeScores.map((score: number | null | '') => Number(score))
          game.data.home_total_score = homeScores.reduce((a: number, b:number) => a + b)
        }
      } else if (teamType === 'away') {
        let awayScores: any[] = [
          game.data.option?.away_1st_score,
          game.data.option?.away_2nd_score,
          game.data.option?.away_ex_1st_score,
          game.data.option?.away_ex_2nd_score
        ]

        const notPlayed = awayScores.every((score: any) => score === null || score === '')

        if (notPlayed) {
          game.data.away_total_score = undefined
        } else {
          awayScores = awayScores.map((score: number | null | '') => Number(score))
          game.data.away_total_score = awayScores.reduce((a: number, b:number) => a + b)
        }
      }
    }

    const scoreSheetUrl = ref(process.env.VUE_APP_API_ENDPOINT+'/sheet/game/' + route.params.game_id)

    useHead({
      title: computed(() => '試合詳細 - ' + season.data?.topic_name + ' ' + season.data?.name)
    })

    const breadcrumbs = computed(() => {
      return [
        {
          type: 'link',
          link: {
            name: '大会一覧',
            path: '/'
          }
        },
        {
          type: 'link',
          size: 'large',
          link: {
            name: season.data?.topic_name,
            secondName: season.data?.name,
            path: `/event/${route.params.topic_id}/season/${route.params.season_id}`,
          },
          setting: {
            path: `/event/${route.params.topic_id}`
          }
        },
        {
          type: 'link',
          link: {
            name: groups.custom?.name,
            path: `/event/${route.params.topic_id}/season/${route.params.season_id}/game?custom=${groups.custom?.id}`,
          }
        },
        {
          type: 'text',
          text: {
            name: '試合情報'
          }
        }
      ] as Breadcrumb[]
    })

    return {
      game,
      getGame,

      updateGame,

      groups,
      getGroups,
      getGroup,

      entries,

      fields,
      getFields,

      onInputScore,

      scoreSheetUrl,

      breadcrumbs
    }
  }
})
